import { useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownWideNarrow as SortNewestIcon,
  ArrowUpNarrowWide as SortOldestIcon,
  RefreshCw as SyncIcon,
  Search as SearchIcon,
  X as XIcon,
} from "lucide-react";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { InboxIconButton, InboxQuickButton } from "../InboxIconButton.js";
import type { CommentsViewMode } from "./comments-url-state.js";
import type {
  SocialCommentHandlingStatusFilter,
  SocialCommentPlatformFilter,
} from "@multi-chat/shared";

export type CommentsListHeaderFilters = {
  viewMode: CommentsViewMode;
  platform?: SocialCommentPlatformFilter;
  handlingStatus: SocialCommentHandlingStatusFilter;
  sort: "activity_desc" | "activity_asc";
  search?: string;
  postId?: string;
};

export function CommentsListHeader({
  filters,
  syncing,
  onViewModeChange,
  onPlatformChange,
  onHandlingStatusChange,
  onSortChange,
  onSearchChange,
  onClearPostFilter,
  onSync,
}: {
  filters: CommentsListHeaderFilters;
  syncing: boolean;
  onViewModeChange: (viewMode: CommentsViewMode) => void;
  onPlatformChange: (platform: SocialCommentPlatformFilter | undefined) => void;
  onHandlingStatusChange: (status: SocialCommentHandlingStatusFilter) => void;
  onSortChange: (sort: "activity_desc" | "activity_asc") => void;
  onSearchChange: (search: string | undefined) => void;
  onClearPostFilter: () => void;
  onSync: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draftSearch, setDraftSearch] = useState(filters.search ?? "");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const oldestFirst = filters.sort === "activity_asc";

  function commitSearch(event: FormEvent): void {
    event.preventDefault();
    const trimmed = draftSearch.trim();
    onSearchChange(trimmed === "" ? undefined : trimmed);
  }

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-inbox-line-soft px-2.5 pb-2.5 pt-2">
      <div className="flex items-center gap-1.5">
        <div
          className="inline-flex flex-1 rounded-lg bg-inbox-rows p-0.5"
          role="tablist"
          aria-label={t("inbox.comments.viewMode.label")}
        >
          <ModeTab
            active={filters.viewMode === "comments"}
            label={t("inbox.comments.viewMode.comments")}
            onClick={() => onViewModeChange("comments")}
          />
          <ModeTab
            active={filters.viewMode === "posts"}
            label={t("inbox.comments.viewMode.posts")}
            onClick={() => onViewModeChange("posts")}
          />
        </div>
        <SimpleTooltip content={t("inbox.comments.sync.tip")}>
          <InboxIconButton
            aria-label={t("inbox.comments.sync.label")}
            disabled={syncing}
            onClick={onSync}
          >
            <SyncIcon
              size={16}
              className={cn(syncing && "animate-spin")}
              aria-hidden="true"
            />
          </InboxIconButton>
        </SimpleTooltip>
      </div>

      <form className="relative" onSubmit={commitSearch}>
        <SearchIcon
          size={14}
          className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-inbox-subtle"
          aria-hidden="true"
        />
        <input
          ref={searchRef}
          type="search"
          value={draftSearch}
          onChange={(event) => setDraftSearch(event.target.value)}
          onBlur={() => {
            const trimmed = draftSearch.trim();
            onSearchChange(trimmed === "" ? undefined : trimmed);
          }}
          className="h-8 w-full rounded-lg border border-inbox-line bg-surface pe-8 ps-8 text-[0.8125rem] text-inbox-strong outline-none placeholder:text-inbox-subtle focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
          placeholder={t("inbox.comments.search.placeholder")}
          aria-label={t("inbox.comments.search.label")}
        />
        {draftSearch ? (
          <button
            type="button"
            className="absolute end-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-md text-inbox-muted hover:bg-inbox-hover hover:text-inbox-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
            aria-label={t("inbox.comments.search.clear")}
            onClick={() => {
              setDraftSearch("");
              onSearchChange(undefined);
              searchRef.current?.focus();
            }}
          >
            <XIcon size={14} aria-hidden="true" />
          </button>
        ) : null}
      </form>

      <div
        className="flex flex-wrap items-center gap-1"
        role="group"
        aria-label={t("inbox.comments.platform.filterLabel")}
      >
        <PlatformChip
          active={filters.platform == null}
          label={t("inbox.comments.platform.all")}
          title={t("inbox.comments.platform.allHint")}
          onClick={() => onPlatformChange(undefined)}
        />
        <PlatformChip
          active={filters.platform === "INSTAGRAM"}
          label={t("channel.instagram")}
          badge="INSTAGRAM"
          onClick={() => onPlatformChange("INSTAGRAM")}
        />
        <PlatformChip
          active={filters.platform === "FACEBOOK"}
          label={t("channel.facebook")}
          badge="FACEBOOK"
          onClick={() => onPlatformChange("FACEBOOK")}
        />
      </div>

      {filters.viewMode === "comments" ? (
        <div className="flex flex-wrap items-center gap-1">
          {(["OPEN", "HANDLED", "ALL"] as const).map((status) => (
            <InboxQuickButton
              key={status}
              active={filters.handlingStatus === status}
              aria-pressed={filters.handlingStatus === status}
              onClick={() => onHandlingStatusChange(status)}
            >
              {t(`inbox.comments.statusFilter.${status.toLowerCase()}`)}
            </InboxQuickButton>
          ))}
          <SimpleTooltip
            content={t(oldestFirst ? "inbox.filters.sortOldestTip" : "inbox.filters.sortNewestTip")}
          >
            <InboxQuickButton
              aria-label={t("inbox.filters.sortToggleLabel", {
                value: t(oldestFirst ? "inbox.sort.oldestFirst" : "inbox.sort.newestFirst"),
              })}
              onClick={() =>
                onSortChange(oldestFirst ? "activity_desc" : "activity_asc")
              }
            >
              {oldestFirst ? (
                <SortOldestIcon size={14} aria-hidden="true" />
              ) : (
                <SortNewestIcon size={14} aria-hidden="true" />
              )}
            </InboxQuickButton>
          </SimpleTooltip>
        </div>
      ) : null}

      {filters.postId ? (
        <div className="flex items-center gap-1.5 rounded-lg bg-brand-mint/60 px-2 py-1 text-[0.75rem] text-primary">
          <span className="min-w-0 flex-1 truncate">
            {t("inbox.comments.postFilter.active")}
          </span>
          <button
            type="button"
            className="shrink-0 font-semibold underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
            onClick={onClearPostFilter}
          >
            {t("inbox.comments.postFilter.clear")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ModeTab({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={cn(
        "h-7 flex-1 rounded-md px-2 text-[0.75rem] font-semibold transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
        active ? "bg-surface text-inbox-strong shadow-sm" : "text-inbox-muted hover:text-inbox-strong",
      )}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function PlatformChip({
  active,
  label,
  badge,
  title,
  onClick,
}: {
  active: boolean;
  label: string;
  badge?: "INSTAGRAM" | "FACEBOOK";
  title?: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <InboxQuickButton
      active={active}
      aria-pressed={active}
      title={title}
      onClick={onClick}
    >
      <span className="inline-flex items-center gap-1">
        {badge ? <ChannelIdentityBadge platformType={badge} size="inline" /> : null}
        {label}
      </span>
    </InboxQuickButton>
  );
}
